import { useState, useEffect, useRef } from 'react'

export default function AddressAutocomplete({ value, onChange, onSelect, placeholder }) {
  const [query, setQuery] = useState(value || '')
  const [suggestions, setSuggestions] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const wrapperRef = useRef(null)
  const debounceRef = useRef(null)

  useEffect(() => {
    setQuery(value || '')
  }, [value])

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const searchAddress = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 3) {
      setSuggestions([])
      return
    }

    setLoading(true)
    try {
      const encoded = encodeURIComponent(searchQuery)
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?access_token=${import.meta.env.VITE_MAPBOX_TOKEN}&autocomplete=true&country=us&bbox=-122.52,37.70,-122.35,37.83&limit=5`
      )

      if (response.ok) {
        const data = await response.json()
        setSuggestions(data.features || [])
        setIsOpen(true)
      }
    } catch (error) {
      console.error('Autocomplete error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const newValue = e.target.value
    setQuery(newValue)
    onChange?.(newValue)

    // Debounce the search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      searchAddress(newValue)
    }, 300)
  }

  const handleSelect = (feature) => {
    const [lng, lat] = feature.center
    const formatted = feature.place_name
    setQuery(formatted)
    setSuggestions([])
    setIsOpen(false)
    onSelect?.({ lat, lng, formatted })
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <input
        type="text"
        className="form-input"
        placeholder={placeholder || "Start typing an address..."}
        value={query}
        onChange={handleInputChange}
        onFocus={() => suggestions.length > 0 && setIsOpen(true)}
      />

      {isOpen && suggestions.length > 0 && (
        <div className="autocomplete-dropdown">
          {suggestions.map((feature) => (
            <div
              key={feature.id}
              className="autocomplete-item"
              onClick={() => handleSelect(feature)}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ flexShrink: 0, opacity: 0.5 }}
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              <span>{feature.place_name}</span>
            </div>
          ))}
        </div>
      )}

      {loading && (
        <div style={{
          position: 'absolute',
          right: '12px',
          top: '50%',
          transform: 'translateY(-50%)'
        }}>
          <div className="spinner" style={{ width: '16px', height: '16px' }}></div>
        </div>
      )}
    </div>
  )
}
